import { Link } from "react-router-dom";
import React, { useState } from "react";
import {
  FiArrowUpRight,
  FiCheckCircle,
  FiGlobe,
  FiLayers,
  FiLock,
  FiMapPin,
  FiPlus,
  FiUsers,
  FiX,
  FiShield,
  FiDatabase,
  FiBarChart2,
  FiSmartphone,
} from "react-icons/fi";

import "../styles/Government.css";

const capabilities = [
  {
    icon: <FiUsers />,
    title: "Citizen experiences",
    text: "Clear digital journeys that help residents discover information, complete tasks, and access essential services with confidence.",
  },
  {
    icon: <FiLock />,
    title: "Secure platforms",
    text: "Reliable digital foundations designed around privacy, governance, role-based access, and operational resilience.",
  },
  {
    icon: <FiGlobe />,
    title: "Accessible by default",
    text: "Inclusive experiences that work across devices, environments, abilities, and different levels of digital confidence.",
  },
  {
    icon: <FiLayers />,
    title: "Connected operations",
    text: "Bring systems, teams, workflows, and information together so public-sector operations can move with greater clarity.",
  },
];

const services = [
  [
    "01",
    "Citizen portals",
    "Self-service platforms for applications, requests, registrations, updates, documents, and support.",
    "Portal",
  ],
  [
    "02",
    "Service modernisation",
    "Modernise priority digital journeys while protecting valuable legacy systems and institutional knowledge.",
    "Modernisation",
  ],
  [
    "03",
    "Case management",
    "Connected workflows that give teams a clearer view of cases, responsibilities, documents, and next actions.",
    "Operations",
  ],
  [
    "04",
    "Workflow automation",
    "Reduce repetitive administration and route work intelligently between departments, teams, and systems.",
    "Automation",
  ],
  [
    "05",
    "Data & dashboards",
    "Turn operational information into useful dashboards that support monitoring, reporting, and decision-making.",
    "Intelligence",
  ],
  [
    "06",
    "Digital accessibility",
    "Design public-facing experiences around usability, accessibility, responsive behaviour, and inclusive interaction.",
    "Accessibility",
  ],
];

const faqs = [
  [
    "What government technology solutions do you provide?",
    "We design public websites, citizen portals, online forms, case-management workflows, reporting dashboards, integrations, mobile experiences, and custom digital platforms.",
  ],
  [
    "Can you modernise a legacy government system?",
    "Yes. Modernisation can be approached in phases, beginning with priority journeys and gradually connecting or replacing legacy components.",
  ],
  [
    "Can you create online forms for public services?",
    "Yes. We can create structured digital forms for applications, registrations, complaints, requests, document submissions, and service updates.",
  ],
  [
    "Can residents track service requests online?",
    "Yes. A citizen portal can provide request status, notifications, documents, updates, and next-step information.",
  ],
  [
    "Can you integrate different departmental systems?",
    "Yes. Relevant systems can be connected through APIs and integration layers to reduce duplicate data entry and improve information flow.",
  ],
  [
    "Can services work well on mobile phones?",
    "Yes. We design responsive experiences so residents can access important information and complete essential tasks from smartphones and tablets.",
  ],
  [
    "How do you approach accessibility and security?",
    "We consider accessibility, secure development practices, authentication, role-based permissions, data protection, and organisational governance throughout the delivery process.",
  ],
];

const impactPoints = [
  "Accessible resident-first experiences",
  "Secure and scalable service foundations",
  "Connected workflows for public-sector teams",
  "Clearer information across departments",
  "Data visibility through practical dashboards",
  "Mobile-friendly digital services",
];

const imageCards = [
  {
    image:
      "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1400&q=85",
    label: "01 / DIGITAL WORKPLACE",
    title: "Connected teams",
  },
  {
    image:
      "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1400&q=85",
    label: "02 / COLLABORATION",
    title: "Better coordination",
  },
  {
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=85",
    label: "03 / DATA INTELLIGENCE",
    title: "Decisions with visibility",
  },
];

export default function Government() {
  const [open, setOpen] = useState(0);

  return (
    <main className="government-page sector-page">
      {/* HERO */}
      <section className="gov-hero">
        <div className="gov-hero-image" />

        <div className="gov-hero-overlay" />

        <div className="gov-hero-content gov-reveal">
          <span className="gov-kicker">
            TECHLYNX / GOVERNMENT & PUBLIC SERVICES
          </span>

          <h1>
            Public services,
            <br />
            made <em>simpler</em>
          </h1>

          <p>
            We design secure, accessible digital services that help public
            organisations connect people, information, and operations through
            better technology.
          </p>

          <div className="gov-hero-actions">
            <a href="#government-solutions" className="gov-button">
              Explore solutions <FiArrowUpRight />
            </a>

            <span className="gov-scroll-note">
              <span />
              Scroll to explore
            </span>
          </div>
        </div>

        <div className="gov-hero-orbit orbit-a" />
        <div className="gov-hero-orbit orbit-b" />

        <div className="gov-hero-status">
          <span className="status-dot" />
          DIGITAL PUBLIC INFRASTRUCTURE
          <b>01</b>
        </div>
      </section>

      {/* INTRO */}
      <section
        id="government-solutions"
        className="gov-intro"
      >
        <div className="gov-intro-grid">
          <div className="gov-section-heading gov-reveal">
            <span className="gov-section-label">
              Government technology
            </span>

            <h2>
              Digital services built for <em>everyone</em>
            </h2>
          </div>

          <div className="gov-intro-copy gov-reveal">
            <p>
              From a resident's first question to a public-sector team's next
              action, technology should remove friction rather than create it.
            </p>

            <p>
              Techlynx connects digital experiences, workflows, data, and
              systems into practical platforms designed around the people who
              use them.
            </p>
          </div>
        </div>

        {/* IMAGE FEATURE */}
        <div className="gov-feature">
          <div className="gov-feature-main">
            <img
              src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1800&q=90"
              alt="Professionals collaborating around a digital project"
            />

            <div className="gov-image-gradient" />

            <div className="gov-feature-caption">
              <span>PUBLIC SECTOR / DIGITAL TRANSFORMATION</span>
              <strong>Technology that connects people and purpose.</strong>
            </div>
          </div>

          <div className="gov-feature-side">
            <div className="gov-mini-image">
              <img
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1000&q=85"
                alt="Digital strategy meeting"
              />

              <div className="gov-mini-label">
                <span>01</span>
                STRATEGY
              </div>
            </div>

            <div className="gov-feature-stat">
              <span>TECHLYNX APPROACH</span>
              <strong>People</strong>
              <strong>Systems</strong>
              <strong>Data</strong>
              <strong>Impact</strong>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="gov-capabilities">
        <div className="gov-section-top">
          <div>
            <span className="gov-section-label">
              What we make possible
            </span>

            <h2>
              Build services people can <em>actually use</em>
            </h2>
          </div>

          <p>
            Public-sector technology needs to be dependable, accessible, and
            understandable. Our approach combines product thinking with
            engineering discipline.
          </p>
        </div>

        <div className="gov-capability-grid">
          {capabilities.map((item, index) => (
            <article
              className="gov-capability-card gov-reveal"
              style={{ "--delay": `${index * 100}ms` }}
              key={item.title}
            >
              <div className="gov-card-number">
                0{index + 1}
              </div>

              <div className="gov-capability-icon">
                {item.icon}
              </div>

              <h3>{item.title}</h3>

              <p>{item.text}</p>

              <span className="gov-card-line" />
            </article>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="gov-services">
        <div className="gov-services-header">
          <div>
            <span className="gov-section-label">
              A connected technology toolkit
            </span>

            <h2>
              One team for your <em>next move</em>
            </h2>
          </div>

          <div className="gov-services-mark">
            <FiShield />
            <span>SECURE</span>
            <span>SCALABLE</span>
            <span>ACCESSIBLE</span>
          </div>
        </div>

        <div className="gov-service-grid">
          {services.map(([number, title, text, tag], index) => (
            <article
              className="gov-service-card gov-reveal"
              style={{ "--delay": `${index * 80}ms` }}
              key={title}
            >
              <div className="gov-service-top">
                <span>{number}</span>
                <FiArrowUpRight />
              </div>

              <small>{tag}</small>

              <h3>{title}</h3>

              <p>{text}</p>

              <div className="gov-service-progress">
                <span />
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* DIGITAL ECOSYSTEM */}
      <section className="gov-ecosystem">
        <div className="gov-ecosystem-copy">
          <span className="gov-section-label">
            Connected public infrastructure
          </span>

          <h2>
            Every service is part of a larger <em>system</em>
          </h2>

          <p>
            A successful public-service platform cannot exist in isolation.
            Websites, mobile experiences, internal systems, databases,
            workflows, and reporting tools need to work together.
          </p>

          <div className="gov-ecosystem-list">
            <div>
              <FiUsers />
              <span>Citizens</span>
            </div>

            <div>
              <FiSmartphone />
              <span>Mobile</span>
            </div>

            <div>
              <FiDatabase />
              <span>Data</span>
            </div>

            <div>
              <FiBarChart2 />
              <span>Insights</span>
            </div>
          </div>
        </div>

        <div className="gov-ecosystem-visual">
          <div className="gov-network-line line-one" />
          <div className="gov-network-line line-two" />
          <div className="gov-network-line line-three" />

          <div className="gov-network-center">
            <FiLayers />
            <span>CONNECTED<br />SERVICES</span>
          </div>

          <div className="gov-network-node node-one">
            <FiUsers />
            <span>People</span>
          </div>

          <div className="gov-network-node node-two">
            <FiDatabase />
            <span>Data</span>
          </div>

          <div className="gov-network-node node-three">
            <FiShield />
            <span>Security</span>
          </div>

          <div className="gov-network-node node-four">
            <FiBarChart2 />
            <span>Insight</span>
          </div>
        </div>
      </section>

      {/* IMAGE MOSAIC */}
      <section className="gov-mosaic">
        <div className="gov-mosaic-header">
          <span className="gov-section-label">
            Designed around real outcomes
          </span>

          <h2>
            From complex systems to <em>clear experiences</em>
          </h2>
        </div>

        <div className="gov-mosaic-grid">
          {imageCards.map((item, index) => (
            <article
              className={`gov-mosaic-card mosaic-${index + 1}`}
              key={item.title}
            >
              <img src={item.image} alt={item.title} />

              <div className="gov-mosaic-overlay" />

              <div className="gov-mosaic-content">
                <span>{item.label}</span>
                <h3>{item.title}</h3>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* RESULTS */}
      <section className="gov-results">
        <div className="gov-results-heading">
          <span className="gov-section-label">
            Built for public impact
          </span>

          <h2>
            Technology that keeps communities <em>moving</em>
          </h2>

          <p>
            We focus on practical digital improvements that make services
            easier to discover, easier to operate, and easier to improve over
            time.
          </p>
        </div>

        <div className="gov-result-points">
          {impactPoints.map((item, index) => (
            <div
              className="gov-result-point gov-reveal"
              style={{ "--delay": `${index * 70}ms` }}
              key={item}
            >
              <span>0{index + 1}</span>
              <FiCheckCircle />
              <p>{item}</p>
              <FiArrowUpRight className="result-arrow" />
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="gov-faq">
        <div className="gov-faq-intro">
          <span className="gov-section-label">
            Frequently asked questions
          </span>

          <h2>
            Questions deserve <em>clear answers</em>
          </h2>

          <p>
            Explore some of the common questions organisations ask when
            planning a new digital public-service experience.
          </p>

          <div className="gov-faq-orbit">
            <FiMessageIcon />
          </div>
        </div>

        <div className="gov-faq-list">
          {faqs.map(([question, answer], index) => (
            <article
              className={`gov-faq-item ${
                open === index ? "is-open" : ""
              }`}
              key={question}
            >
              <button
                type="button"
                onClick={() =>
                  setOpen(open === index ? -1 : index)
                }
                aria-expanded={open === index}
              >
                <span>
                  <small>0{index + 1}</small>
                  {question}
                </span>

                <i>
                  {open === index ? <FiX /> : <FiPlus />}
                </i>
              </button>

              <div
                className={`gov-faq-answer ${
                  open === index ? "show" : ""
                }`}
              >
                <p>{answer}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CLOSING */}
      <section className="gov-closing">
        <div className="gov-closing-image">
          <img
            src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1800&q=90"
            alt="Modern technology workspace"
          />
        </div>

        <div className="gov-closing-overlay" />

        <div className="gov-closing-content">
          <span className="gov-section-label">
            Build a stronger digital future
          </span>

          <h2>
            Turn public-service challenges into{" "}
            <em>digital opportunity</em>
          </h2>

          <p>
            Whether you need a new platform, connected systems, workflow
            automation, or a clear technology roadmap, Techlynx can help turn
            your ambition into a scalable digital product.
          </p>

          <Link to="/contact" className="gov-button">
            Start a project <FiArrowUpRight />
          </Link>
        </div>

        <div className="gov-closing-grid" />
      </section>
    </main>
  );
}

function FiMessageIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="42"
      height="42"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    >
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.8 9.8 0 0 1-4-.8L3 21l1.8-4.1A8.2 8.2 0 0 1 3 11.5a8.5 8.5 0 0 1 9-8.5 8.5 8.5 0 0 1 9 8.5Z" />
      <path d="M8 11.5h.01M12 11.5h.01M16 11.5h.01" />
    </svg>
  );
}